import React from 'react';
import { Field, Form, Formik, FormikHelpers } from 'formik';
import { TextField } from 'formik-material-ui';
import * as Yup from 'yup';

import Button from '@material-ui/core/Button';
import Container from '@material-ui/core/Container';
import CloseIcon from '@material-ui/icons/Close';
import IconButton from '@material-ui/core/IconButton';
import Grid from '@material-ui/core/Grid';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import * as UserService from '../../api/userService';

import FullScreenDialog, { FullScreenDialogProps } from '../FullScreenDialog';
import useCompanyContext from '../../hooks/useCompanyContext';

const useStyles = makeStyles((theme) => ({
  content: {
    [theme.breakpoints.up('sm')]: {
      width: 400,
      margin: '0 auto',
      marginTop: theme.spacing(2),
    },
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
      margin: '0 auto',
      marginTop: theme.spacing(2),
    },
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: theme.spacing(6),
  },
  header: {
    marginTop: theme.spacing(2),
  },
  actionContainer: {
    marginTop: 12,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  box: {
    flex: 1,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  close: {
    marginRight: 'auto',
  },
  closeIcon: {
    color: theme.palette.common.black,
  },
  titleContainer: {
    marginRight: 10,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
}));

export interface Values {
  email: string;
}

type Props = Pick<FullScreenDialogProps, 'open'> & {
  onClose: () => void;
  onSuccess: () => void;
  currentEmail: string;
  userId: string;
};

export default function UpdateEmailDialog({
  open,
  onClose,
  onSuccess,
  currentEmail,
  userId,
}: Props) {
  const classes = useStyles();
  const { company } = useCompanyContext();
  const containsPrivateRelayAddress = (value: string) =>
    value.includes('@privaterelay.appleid.com') ? true : false;

  const initialValues: Values = {
    email: currentEmail,
  };

  const ValidationSchema = Yup.object().shape({
    email: Yup.string()
      .email('Not a valid email')
      .required('Email is required')
      .test(
        'excludePrivateRelayAddress',
        'Should not contain private relay address',
        (value) => (value ? !containsPrivateRelayAddress(value) : false)
      ),
  });

  const handleSubmit = async (
    values: Values,
    { setFieldError, setSubmitting }: FormikHelpers<Values>
  ) => {
    if (company && userId) {
      try {
        await UserService.updateUserEmail({
          companyId: company.id,
          email: values.email,
          userID: userId,
        });
        onSuccess();
        setSubmitting(false);
      } catch (e: any) {
        console.log('Error: ', e);
        e.data.forEach((err: any) => {
          setFieldError(err.meta.attribute, err.detail);
        });
        setSubmitting(false);
      }
    }
  };

  // TODO - Display a loader
  if (!company) {
    return null;
  }

  return (
    <FullScreenDialog open={open}>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={ValidationSchema}
        onSubmit={handleSubmit}
        validateOnChange={false}
        validateOnBlur={false}
      >
        {({ isSubmitting, setFieldValue }) => (
          <Form noValidate>
            <div className={classes.box}>
              <IconButton onClick={onClose}>
                <CloseIcon className={classes.closeIcon} />
              </IconButton>
            </div>
            <Grid container>
              <Grid item xs={12}>
                <div className={classes.content}>
                  <Container maxWidth="md">
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <div className={classes.titleContainer}>
                          <Typography variant="h4">Edit Email</Typography>
                        </div>
                      </Grid>
                      <Grid item xs={12}>
                        <Field
                          component={TextField}
                          name="email"
                          label="Email"
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <div className={classes.actionContainer}>
                          <Button onClick={onClose}>Cancel</Button>
                          <Button
                            type="submit"
                            color="primary"
                            disabled={isSubmitting}
                          >
                            Save
                          </Button>
                        </div>
                      </Grid>
                    </Grid>
                  </Container>
                </div>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </FullScreenDialog>
  );
}
